import { ReactNode, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard, Building2, Heart, User as UserIcon,
  LogOut, Bell, Menu, KanbanSquare,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { LayoutContextProvider, useLayoutCtx, useLayoutSync } from "./LayoutContext";

interface NavItem { label: string; href: string; icon: typeof LayoutDashboard; }

const navItems: NavItem[] = [
  { label: "Dashboard", href: "/investor", icon: LayoutDashboard },
  { label: "Browse Startups", href: "/investor/browse", icon: Building2 },
  { label: "Interested", href: "/investor/interested", icon: Heart },
  { label: "Deals & Pipeline", href: "/investor/deals", icon: KanbanSquare },
  { label: "Profile", href: "/investor/profile", icon: UserIcon },
];

export function InvestorLayoutChrome({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { title } = useLayoutCtx();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isActive = (href: string) =>
    href === "/investor" ? location.pathname === href : location.pathname.startsWith(href);

  const handleLogout = () => { logout(); navigate("/login"); };

  const Sidebar = () => (
    <div className="flex flex-col h-full bg-navy">
      <div className="flex items-center px-5 h-16 border-b border-white/10 shrink-0">
        <span className="text-white font-semibold text-sm">Investor Portal</span>
      </div>

      <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <Link
            key={item.href}
            to={item.href}
            onClick={() => setMobileOpen(false)}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
              isActive(item.href) ? "bg-white/10 text-white" : "text-white/70 hover:text-white hover:bg-white/5"
            }`}
          >
            <item.icon size={18} className="shrink-0" />
            {item.label}
          </Link>
        ))}
      </nav>

      <div className="border-t border-white/10 p-3 shrink-0 space-y-1">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center shrink-0">
            <span className="text-gold text-xs font-bold">{user?.name?.split(" ").map(n => n[0]).join("").slice(0, 2) || "I"}</span>
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.name || "Investor"}</p>
            <p className="text-white/40 text-xs truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-white/70 hover:text-red-400 hover:bg-white/5 transition-colors"
        >
          <LogOut size={18} />Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <aside className="hidden lg:flex w-64 shrink-0"><Sidebar /></aside>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setMobileOpen(false)}
              className="lg:hidden fixed inset-0 bg-black/60 z-40"
            />
            <motion.aside
              initial={{ x: -260 }} animate={{ x: 0 }} exit={{ x: -260 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="lg:hidden fixed left-0 top-0 bottom-0 w-64 z-50"
            >
              <Sidebar />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 sm:px-6 shrink-0">
          <div className="flex items-center gap-3">
            <button type="button" aria-label="Open menu" onClick={() => setMobileOpen(true)} className="lg:hidden text-gray-500 hover:text-black">
              <Menu size={22} />
            </button>
            <h1 className="text-base font-semibold text-black">{title || "Investor Portal"}</h1>
          </div>
          <div className="flex items-center gap-3">
            <button type="button" aria-label="Notifications" className="text-gray-500 hover:text-black p-2"><Bell size={18} /></button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gold/20 flex items-center justify-center">
                <span className="text-gold text-xs font-bold">{user?.name?.charAt(0) || "I"}</span>
              </div>
              <div className="hidden sm:block">
                <p className="text-sm font-medium text-black leading-tight">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6">{children}</main>
      </div>
    </div>
  );
}

export default function InvestorLayout({ children, title }: { children: ReactNode; title?: string }) {
  const ctx = useLayoutCtx();
  // If we're already inside a layout-route, just sync the title and pass children through.
  if (ctx.mounted) {
    return <InvestorLayoutTitleSync title={title}>{children}</InvestorLayoutTitleSync>;
  }
  // Standalone use (legacy or non-layout-route paths).
  return (
    <LayoutContextProvider defaultTitle={title}>
      <InvestorLayoutChrome>{children}</InvestorLayoutChrome>
    </LayoutContextProvider>
  );
}

function InvestorLayoutTitleSync({ children, title }: { children: ReactNode; title?: string }) {
  useLayoutSync(title);
  return <>{children}</>;
}
